/**
 * Discovery grid: cell status and ball-in-court derivation (Phase A of
 * docs/DISCOVERY_GRID_REDESIGN_SPEC.md).
 *
 * Phase A keeps the grid's existing five statuses but fixes their meaning:
 * an unanswered question is `not_started` (it used to be stamped
 * `waiting_consultant_response` and shown as "In Progress"). The owner of a
 * cell is derived from its status, never stored (spec §4.4). Phase B/C
 * replace these statuses with the six-level ladder.
 */

import { isLocked, normalizeStatus } from './configItemLifecycle';

export type GridCellStatus =
  | 'not_started'
  | 'waiting_consultant_response' // artifacts in progress only (no question cell carries it)
  | 'completed'
  | 'blocked'
  | 'ready_for_build';

export type GridCellKind = 'question' | 'shadow';

export type CellOwner = 'client' | 'consultant' | 'none';

/** True when a stored answer counts as answered (empty strings and empty arrays do not). */
export function hasAnswer(answer: unknown): boolean {
  if (answer === undefined || answer === null) return false;
  if (typeof answer === 'string') return answer.trim() !== '';
  if (Array.isArray(answer)) return answer.length > 0;
  return true; // numbers (including 0), booleans (including false), objects
}

/**
 * Status of one question cell from its config item's lifecycle status (any spelling) and the
 * stored answer. A blocked item is blocked; a locked item (ready_for_build and after) is locked.
 */
export function questionCellStatus(itemStatus: string | undefined, answer: unknown): GridCellStatus {
  const lifecycle = normalizeStatus(itemStatus);
  if (lifecycle === 'blocked') return 'blocked';
  if (isLocked(lifecycle)) return 'ready_for_build';
  return hasAnswer(answer) ? 'completed' : 'not_started';
}

/**
 * Who the ball is with for a cell (spec §4.4, Phase A form):
 * - anything the client still has to do is the client's;
 * - an answer nobody has checked is the consultant's (checked marks arrive in Phase C);
 * - locked cells and complete artifacts have no owner;
 * - blockers are firm-side gating actions, so the consultant's.
 */
export function cellOwner(status: GridCellStatus, kind: GridCellKind): CellOwner {
  switch (status) {
    case 'blocked':
      return 'consultant';
    case 'not_started':
    case 'waiting_consultant_response':
      return 'client';
    case 'completed':
      return kind === 'question' ? 'consultant' : 'none';
    case 'ready_for_build':
      return 'none';
    default:
      return 'none';
  }
}

export const CELL_OWNER_LABELS: Record<CellOwner, string> = {
  client: 'Client',
  consultant: 'Consultant',
  none: 'No action needed',
};

/** Values accepted by the grid's ball-in-court filter. */
export const BALL_IN_COURT_FILTERS = ['all', 'client', 'consultant', 'none'] as const;
export type BallInCourtFilter = (typeof BALL_IN_COURT_FILTERS)[number];

export function normalizeBallInCourtFilter(value: string | undefined | null): BallInCourtFilter {
  return (BALL_IN_COURT_FILTERS as readonly string[]).includes(value ?? '') ? (value as BallInCourtFilter) : 'all';
}

export function matchesBallInCourt(owner: CellOwner, filter: BallInCourtFilter): boolean {
  return filter === 'all' || owner === filter;
}
